import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Table,
  Tag,
  Space,
  Skeleton,
  message,
} from "antd";
import CountUp from "react-countup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { apiGet } from "../api/request";
import apiRoutes from "../api/apiRoutes";

import SubscriptionTrendChart from "../charts/SubscriptionTrendChart";
import ExpiryRadialChart from "../charts/ExpiryRadialChart";
import BookStatusPie from "../charts/BookStatusPie";
import AiInsightPanel from "../charts/AiInsightPanel";

dayjs.extend(utc);

const { RangePicker } = DatePicker;

//////////////////////////////////////////////////////
// ✨ ANIMATED NUMBER
//////////////////////////////////////////////////////

const AnimatedNumber = ({ value }) => (
  <CountUp end={Number(value || 0)} duration={1.2} separator="," />
);

//////////////////////////////////////////////////////
// ✨ KPI CARD
//////////////////////////////////////////////////////

const StatCard = ({ title, value, color, loading }) => {
  return (
    <Card className="glass-card">
      {loading ? (
        <Skeleton active paragraph={false} />
      ) : (
        <Statistic
          title={title}
          valueRender={() => (
            <span className="kpi-value" style={{ color: color || "#111827" }}>
              <AnimatedNumber value={value} />
            </span>
          )}
        />
      )}
    </Card>
  );
};

//////////////////////////////////////////////////////
// COMPONENT
//////////////////////////////////////////////////////

const Home = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [subscriptionData, setSubscriptionData] = useState({});
  const [recentBooks, setRecentBooks] = useState([]);
  const [expiringUsers, setExpiringUsers] = useState([]);

  const [dateRange, setDateRange] = useState([
    dayjs().subtract(60, "day"),
    dayjs(),
  ]);

  //////////////////////////////////////////////////////
  // API CALLS
  //////////////////////////////////////////////////////

  const fetchSummary = async () => {
    try {
      const data = await apiGet(apiRoutes.DASHBOARD_SUMMARY);
      setSummary(data || {});
    } catch {
      message.error("Failed to load summary");
    }
  };

  const fetchSubscriptions = async (from, to) => {
    try {
      const data = await apiGet(
        `${apiRoutes.DASHBOARD_SUBSCRIPTIONS}?from=${from}&to=${to}`,
      );
      setSubscriptionData(data || {});
    } catch {
      message.error("Failed to load subscription data");
    }
  };

  const fetchRecentBooks = async () => {
    try {
      const data = await apiGet(apiRoutes.DASHBOARD_RECENT_BOOKS);
      setRecentBooks(data || []);
    } catch {
      message.error("Failed to load recent books");
    }
  };

  const fetchExpiringUsers = async () => {
    try {
      const res = await apiGet(`${apiRoutes.DASHBOARD_EXPIRING_USERS}?days=30`);
      const users = Array.isArray(res) ? res : [];
      setExpiringUsers(users);
    } catch {
      message.error("Failed to load expiring users");
    }
  };

  //////////////////////////////////////////////////////
  // INITIAL LOAD
  //////////////////////////////////////////////////////

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line
  }, []);

  const loadDashboard = async () => {
    setLoading(true);

    const from = dateRange[0].format("YYYY-MM-DD");
    const to = dateRange[1].format("YYYY-MM-DD");

    await Promise.all([
      fetchSummary(),
      fetchSubscriptions(from, to),
      fetchRecentBooks(),
      fetchExpiringUsers(),
    ]);

    setLoading(false);
  };

  //////////////////////////////////////////////////////
  // DATE FILTER
  //////////////////////////////////////////////////////

  const handleDateChange = (dates) => {
    if (!dates) return;

    setDateRange(dates);

    const from = dates[0].format("YYYY-MM-DD");
    const to = dates[1].format("YYYY-MM-DD");

    fetchSubscriptions(from, to);
  };

  //////////////////////////////////////////////////////
  // TABLE COLUMNS
  //////////////////////////////////////////////////////

  const subscriptionColumns = [
    { title: "User", dataIndex: "user_name" },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => {
        const color =
          s?.toLowerCase() === "annual"
            ? "green"
            : s?.toLowerCase() === "trial"
              ? "blue"
              : "red";
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      render: (v) => dayjs(v).format("DD MMM YYYY"),
    },
  ];

  const booksColumns = [
    { title: "Book Name", dataIndex: "title" },
    {
      title: "Added On",
      dataIndex: "created_at",
      render: (v) => dayjs(v).format("DD MMM YYYY"),
    },
  ];

  const expiringColumns = [
    { title: "User", dataIndex: "user_name" },
    {
      title: "Expiry Date",
      dataIndex: "end_date",
      render: (v) => dayjs(v).format("DD MMM YYYY"),
    },
    {
      title: "Days Left",
      render: (_, row) => {
        const days = dayjs(row.end_date).diff(dayjs(), "day");

        let color = "green";
        if (days <= 1) color = "red";
        else if (days <= 3) color = "orange";

        return <Tag color={color}>{days} days</Tag>;
      },
    },
  ];

  //////////////////////////////////////////////////////
  // UI
  //////////////////////////////////////////////////////

  return (
    <div className="dashboard-root" style={{ padding: 16 }}>
      {/* HEADER */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Dashboard</h2>
        <Space>
          <RangePicker value={dateRange} onChange={handleDateChange} />
        </Space>
      </Row>

      {/* ================= KPI + RADIAL ================= */}

      <Row gutter={[16, 16]} align="stretch">
        {/* LEFT — KPI GRID */}
        <Col xs={24} lg={16}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <StatCard
                title="Total Books"
                value={summary.total_books}
                loading={loading}
              />
            </Col>

            <Col span={8}>
              <StatCard
                title="Active Books"
                value={summary.active_books}
                color="#16a34a"
                loading={loading}
              />
            </Col>

            <Col span={8}>
              <StatCard
                title="Inactive Books"
                value={summary.inactive_books}
                color="#dc2626"
                loading={loading}
              />
            </Col>

            <Col span={8}>
              <StatCard
                title="Total Users"
                value={summary.total_users}
                loading={loading}
              />
            </Col>

            <Col span={8}>
              <StatCard
                title="Active Users"
                value={summary.active_users}
                color="#2563eb"
                loading={loading}
              />
            </Col>

            <Col span={8}>
              <StatCard
                title="Expiring Users"
                value={expiringUsers?.length}
                color="#f59e0b"
                loading={loading}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <AiInsightPanel
                summary={summary}
                expiringUsers={expiringUsers}
                trend={subscriptionData?.trend || []}
              />
            </Col>
          </Row>
        </Col>

        {/* RIGHT — RADIAL */}
        <Col xs={24} lg={8} style={{ display: "flex" }}>
          <Card
            title="Subscription Expiry Funnel"
            className="glass-card"
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
            bodyStyle={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ExpiryRadialChart users={expiringUsers} />
          </Card>
        </Col>
      </Row>

      {/* ================= EXPIRING TABLE ================= */}

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Expiring Users" className="glass-card">
            <Table
              rowKey="id"
              size="small"
              pagination={{ pageSize: 5 }}
              dataSource={expiringUsers}
              columns={expiringColumns}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      {/* ================= PIE + TREND ================= */}

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={8}>
          <Card title="Book Status" className="glass-card">
            <BookStatusPie
              active={summary.active_books}
              inactive={summary.inactive_books}
            />
          </Card>
        </Col>

        <Col span={16}>
          <Card title="Subscription Trend" className="glass-card">
            <SubscriptionTrendChart data={subscriptionData?.trend || []} />
          </Card>
        </Col>
      </Row>

      {/* ================= RECENT TABLES ================= */}

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Card title="Recent Subscriptions" className="glass-card">
            <Table
              rowKey="id"
              size="small"
              pagination={{ pageSize: 5 }}
              dataSource={subscriptionData?.recent || []}
              columns={subscriptionColumns}
              loading={loading}
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Recently Added Books" className="glass-card">
            <Table
              rowKey="id"
              size="small"
              pagination={{ pageSize: 5 }}
              dataSource={recentBooks}
              columns={booksColumns}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Home;
